import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from './ProtectedRoute';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { SchoolsPage } from '@/pages/SchoolsPage';
import { AccountsPage } from '@/pages/AccountsPage';
import { AccountFormPage } from '@/pages/accounts';
import { ModulesPage, ModuleDetailPage } from '@/pages/modules';
import {
  ProductsPage,
  ProductDetailPage,
  ProductFormPage,
  OfferDetailPage,
  OfferFormPage,
  OptionFormPage,
} from '@/pages/products';
import { SchoolYearsPage, SchoolYearFormPage } from '@/pages/school-years';
import { SchoolCreatePage, SchoolUpgradeV2Page, SchoolEditPage, SchoolDetailPage, SchoolsMapPage, ImportSessionDetailPage, ImportReportPage } from '@/pages/schools';
import {
  SchoolNetworksPage,
  SchoolNetworkDetailPage,
  SchoolNetworkFormPage,
} from '@/pages/school-networks';
import { WhiteLabelsPage, WhiteLabelDetailPage } from '@/pages/white-labels';
import {
  MigrationsPage,
  MigrationDetailPage,
  MigrationFormPage,
} from '@/pages/migrations';
import { LoginPhotosPage } from '@/pages/login-photos/LoginPhotosPage';
import { LoginPhotoFormPage } from '@/pages/login-photos/LoginPhotoFormPage';
import { LoginPhotosCalendarPage } from '@/pages/login-photos/LoginPhotosCalendarPage';
import { DatabasesPage } from '@/pages/databases';
import { ExportsPage } from '@/pages/exports';
import { AdminPermissionsPage } from '@/pages/admin-permissions';
import { AdministratorsPage, AdministratorFormPage } from '@/pages/administrators';
import { NotificationsPage, NotificationFormPage } from '@/pages/notifications';
import { SearchPage } from '@/pages/search';
import { PilotagePage } from '@/pages/pilotage';
import { SchoolImportV2Page } from '@/pages/school-import-v2';
import {
  MigrationV1V2Page,
  ProjectsListPage,
  ProjectDetailPage,
  ProjectReportPage,
  StepDetailPage,
  ConfigPage as MigrationConfigPage,
  BoardPage as MigrationBoardPage,
  AutoCatchUpPage,
  AutoCatchUpSweepPage,
  AutoCatchUpSchoolPage,
} from '@/pages/migration-v1-v2';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    // Pilotage en mode écran TV : plein écran, sans menu ni en-tête ADM
    path: '/pilotage/tv',
    element: (
      <ProtectedRoute requiredPermissions={['sidebar.dashboard_live']}>
        <PilotagePage tv />
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'pilotage',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.dashboard_live']}>
            <PilotagePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'schools',
        element: <SchoolsPage />,
      },
      {
        path: 'schools/map',
        element: <SchoolsMapPage />,
      },
      {
        path: 'schools/new',
        element: <SchoolCreatePage />,
      },
      {
        path: 'schools/:code',
        element: <SchoolDetailPage />,
      },
      {
        path: 'schools/:code/edit',
        element: <SchoolEditPage />,
      },
      {
        path: 'schools/:code/upgrade-v2',
        element: (
          <ProtectedRoute requiredPermissions={['action.v2.deploy']}>
            <SchoolUpgradeV2Page />
          </ProtectedRoute>
        ),
      },
      {
        path: 'schools/:code/import-v2',
        element: (
          <ProtectedRoute requiredPermissions={['action.v2.import']}>
            <SchoolImportV2Page />
          </ProtectedRoute>
        ),
      },
      {
        // Détail d'un import de l'assistant (historique du bloc « Données »)
        path: 'schools/:code/imports/:sessionId',
        element: (
          <ProtectedRoute requiredPermissions={['action.v2.import']}>
            <ImportSessionDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        // Rapport complet d'un import (analyse IA, imprimable / export PDF)
        path: 'schools/:code/imports/:sessionId/report',
        element: (
          <ProtectedRoute requiredPermissions={['action.v2.import']}>
            <ImportReportPage />
          </ProtectedRoute>
        ),
      },
      // School Networks routes
      {
        path: 'school-networks',
        element: <SchoolNetworksPage />,
      },
      {
        path: 'school-networks/new',
        element: <SchoolNetworkFormPage />,
      },
      {
        path: 'school-networks/:id',
        element: <SchoolNetworkDetailPage />,
      },
      // White Labels routes
      {
        path: 'white-labels',
        element: <WhiteLabelsPage />,
      },
      {
        // La création autonome n'existe plus : une marque blanche naît avec
        // son réseau physique
        path: 'white-labels/new',
        element: <Navigate to="/school-networks/new?type=physical" replace />,
      },
      {
        path: 'white-labels/:id',
        element: <WhiteLabelDetailPage />,
      },
      {
        path: 'accounts',
        element: <AccountsPage />,
      },
      {
        path: 'accounts/new',
        element: <AccountFormPage />,
      },
      {
        path: 'accounts/:id/edit',
        element: <AccountFormPage />,
      },
      // Modules routes
      {
        path: 'modules',
        element: <ModulesPage />,
      },
      {
        path: 'modules/:code',
        element: <ModuleDetailPage />,
      },
      // Products routes
      {
        path: 'products',
        element: <ProductsPage />,
      },
      {
        path: 'products/new',
        element: <ProductFormPage />,
      },
      {
        path: 'products/:code',
        element: <ProductDetailPage />,
      },
      {
        path: 'products/:code/edit',
        element: <ProductFormPage />,
      },
      {
        path: 'products/:productCode/offers/new',
        element: <OfferFormPage />,
      },
      {
        path: 'products/:productCode/offers/:offerCode',
        element: <OfferDetailPage />,
      },
      {
        path: 'products/:productCode/offers/:offerCode/edit',
        element: <OfferFormPage />,
      },
      {
        path: 'products/:productCode/options/new',
        element: <OptionFormPage />,
      },
      {
        path: 'products/:productCode/options/:optionId',
        element: <OptionFormPage />,
      },
      // School Years routes
      {
        path: 'school-years',
        element: <SchoolYearsPage />,
      },
      {
        path: 'school-years/new',
        element: <SchoolYearFormPage />,
      },
      {
        path: 'school-years/:id/edit',
        element: <SchoolYearFormPage />,
      },
      // Migrations routes
      {
        path: 'migrations',
        element: <MigrationsPage />,
      },
      {
        path: 'migrations/new',
        element: <MigrationFormPage />,
      },
      {
        path: 'migrations/:id',
        element: <MigrationDetailPage />,
      },
      {
        path: 'migrations/:id/edit',
        element: <MigrationFormPage />,
      },
      // Login Photos routes
      {
        path: 'login-photos',
        element: <LoginPhotosPage />,
      },
      {
        path: 'login-photos/calendar',
        element: <LoginPhotosCalendarPage />,
      },
      {
        path: 'login-photos/new',
        element: <LoginPhotoFormPage />,
      },
      {
        path: 'login-photos/:id/edit',
        element: <LoginPhotoFormPage />,
      },
      // Databases route
      {
        path: 'databases',
        element: <DatabasesPage />,
      },
      // Exports route
      {
        path: 'exports',
        element: <ExportsPage />,
      },
      // Admin Permissions route
      {
        path: 'admin-permissions',
        element: <AdminPermissionsPage />,
      },
      // Administrators routes
      {
        path: 'administrators',
        element: <AdministratorsPage />,
      },
      {
        path: 'administrators/new',
        element: <AdministratorFormPage />,
      },
      {
        path: 'administrators/:id/edit',
        element: <AdministratorFormPage />,
      },
      // Notifications routes
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },
      {
        path: 'notifications/new',
        element: <NotificationFormPage />,
      },
      {
        path: 'notifications/:id/edit',
        element: <NotificationFormPage />,
      },
      // Search route
      {
        path: 'search',
        element: <SearchPage />,
      },
      // Migration V1 → V2 routes
      {
        path: 'migration-v1-v2',
        element: <Navigate to="/migration-v1-v2/dashboard" replace />,
      },
      {
        path: 'migration-v1-v2/dashboard',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <MigrationV1V2Page />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/board',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <MigrationBoardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/schools',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <ProjectsListPage />
          </ProtectedRoute>
        ),
      },
      // Rattrapage automatique : déclaré avant `migration-v1-v2/:id` (les
      // segments statiques priment de toute façon dans React Router).
      {
        path: 'migration-v1-v2/rattrapage',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <AutoCatchUpPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/rattrapage/:sweepId',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <AutoCatchUpSweepPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/rattrapage/ecoles/:projectId',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <AutoCatchUpSchoolPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/config',
        element: (
          <ProtectedRoute requiredPermissions={['action.migration_v1_v2.config']}>
            <MigrationConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/:id',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <ProjectDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/:id/report',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <ProjectReportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'migration-v1-v2/:id/steps/:stepId',
        element: (
          <ProtectedRoute requiredPermissions={['sidebar.migration_v1_v2']}>
            <StepDetailPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

export function AppRouter() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
